import React, { useState, useCallback, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Link from '@material-ui/core/Link';

import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebaseConfig';

import MultiUseDialog from '../Components/MultiUseDialog';
import ProgressIndicator from '../Components/ProgressIndicator';

import {
  HandleSignIn,
  GetScopesForSignIn,
  GetScopesForSignInAndConnect,
  GetRedirectUriForSignIn,
  GetQuickBooksSignInState,
  GetQuickBooksStateFromQueryString,
  GetQuickBooksRealmIDFromQueryString,
  GetQuickBooksAuthCodeFromQueryString,
  GetQuickBooksErrorFromQueryString,
} from '../Util/QuickBooks';

import {
  GetUserHomePath,
} from '../Util/PathHelper';
import {
  GetBrowserFingerprint,
} from '../Util/SignIn';
import {
  clearMarketingAttribution,
  readMarketingAttribution,
} from '../Util/MarketingAttribution';
import { pushMarketingEvent } from '../Util/MarketingEvents';
import { IsMobile } from '../Util/MobileDetector';
import API, {
  GetUserIntuitAuthPathForApi,
  GetUserFirebaseSigninPathForApi,
  GetUserMFAVerifyTOTPPathForApi,
  GetUserMFAResetRecoveryPathForApi,
} from '../Util/api';
import {
  GetTextWithUrlsAsRawAnchors,
  GetBodyHtmlWithModifiedAnchors,
} from '../Util/Regex';

const styles = theme => ({
  root: {
    backgroundColor: theme.palette.background.default,
    height: "100%",
    display:"flex",
    overflow:"hidden",
  },
});

const _browserFingerprint = GetBrowserFingerprint();

const IntuitSignIn = ({ classes, theme, match, location, history }) => {
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const [dialogDetails, setDialogDetails] = useState({ Open: false });
  const [showProgressIndicator, setShowProgressIndicator] = useState(true);

  // Server-side TOTP verification (for custom token sign-ins)
  const [showTOTPDialog, setShowTOTPDialog] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [pendingFirebaseUser, setPendingFirebaseUser] = useState(null);
  const [pendingIntuitSignInInfo, setPendingIntuitSignInInfo] = useState(null);
  const [totpShowRecovery, setTotpShowRecovery] = useState(false);
  const [totpRecoveryCode, setTotpRecoveryCode] = useState('');
  const [totpRecoveryLoading, setTotpRecoveryLoading] = useState(false);
  const [totpRecoverySuccess, setTotpRecoverySuccess] = useState(false);

  // Parse query string params
  const quickBooksSignInState = GetQuickBooksStateFromQueryString({ location });
  const quickBooksAuthorizationCode = GetQuickBooksAuthCodeFromQueryString({ location });
  const quickBooksRealmID = GetQuickBooksRealmIDFromQueryString({ location });
  const quickBooksError = GetQuickBooksErrorFromQueryString({ location });

  const tryCloseWindowOrRedirectToRoot = useCallback(() => {
    window.close();
    history.replace(GetUserHomePath());
  }, [history]);

  const handleApiError = useCallback((err) => {
    setShowProgressIndicator(false);
    const msgWithAnchors = (err.response?.data?.message)
      ? GetTextWithUrlsAsRawAnchors(err.response.data.message.replaceAll("\n","<br />"), theme)
      : null;
    setDialogDetails({
      Open: true,
      Title: "Sorry, something went wrong...",
      BodyContent: (msgWithAnchors)
        ? "<div style=\"font-size:16px\">"
          + GetBodyHtmlWithModifiedAnchors(msgWithAnchors, theme, true)
          + "</div>"
        : "",
      BodyText: (!msgWithAnchors)
        ? "Please try again."
        : null,
      BodyContentIsRawHtml: Boolean(msgWithAnchors),
      CloseCallback: () => tryCloseWindowOrRedirectToRoot(),
    });
  }, [theme, tryCloseWindowOrRedirectToRoot]);

  // Main Firebase login function
  const doFirebaseLogin = useCallback(async (firebaseUser, mfaToken, intuitSignInInfo) => {
    try {
      const idToken = await firebaseUser.getIdToken();
      const marketingAttribution = readMarketingAttribution();

      const response = await API.post(GetUserFirebaseSigninPathForApi(), {
        idToken,
        browserFingerprint: _browserFingerprint,
        isMobile: IsMobile(),
        mfaToken: mfaToken || undefined,
        intuitSignInInfo: intuitSignInInfo || undefined,
        marketingAttribution: marketingAttribution || undefined,
        referrer: marketingAttribution?.referrer || undefined,
      }, {
        withCredentials: true,
      });

      if (response.status === 200) {
        pushMarketingEvent({
          event: response.data?.IsNewUser ? 'sign_up' : 'login',
          method: 'intuit',
        });
        if (marketingAttribution) {
          clearMarketingAttribution();
        }
        setRedirectToReferrer(true);
      }
    } catch (err) {
      // Check for server-side MFA verification required
      if (err.response?.status === 403 && err.response?.data?.error === 'mfa_verification_required') {
        setPendingFirebaseUser(firebaseUser);
        setPendingIntuitSignInInfo(intuitSignInInfo);
        setShowTOTPDialog(true);
        setShowProgressIndicator(false);
        return;
      }
      handleApiError(err);
    }
  }, [handleApiError]);

  // Server-side TOTP verification
  const handleTOTPVerify = useCallback(async () => {
    if (totpCode.length !== 6) {
      setTotpError('Please enter a 6-digit code.');
      return;
    }

    setTotpLoading(true);
    setTotpError('');

    try {
      const idToken = await pendingFirebaseUser.getIdToken();
      const response = await API.post(GetUserMFAVerifyTOTPPathForApi(), {
        idToken,
        totpCode,
      });

      setShowTOTPDialog(false);
      setTotpCode('');
      setShowProgressIndicator(true);
      await doFirebaseLogin(pendingFirebaseUser, response.data.verificationToken, pendingIntuitSignInInfo);
      setPendingFirebaseUser(null);
      setPendingIntuitSignInInfo(null);
    } catch (err) {
      console.error('TOTP verification error:', err);
      if (err.response?.status === 401) {
        setTotpError('Invalid code. Please check your authenticator app and try again.');
      } else {
        setTotpError('Verification failed. Please try again.');
      }
    } finally {
      setTotpLoading(false);
    }
  }, [totpCode, pendingFirebaseUser, pendingIntuitSignInInfo, doFirebaseLogin]);

  const handleTOTPCancel = useCallback(() => {
    setShowTOTPDialog(false);
    setTotpCode('');
    setTotpError('');
    setPendingFirebaseUser(null);
    setPendingIntuitSignInInfo(null);
    setTotpShowRecovery(false);
    setTotpRecoveryCode('');
    setTotpRecoverySuccess(false);
    tryCloseWindowOrRedirectToRoot();
  }, [tryCloseWindowOrRedirectToRoot]);

  const handleTOTPRecoveryVerify = useCallback(async () => {
    if (!totpRecoveryCode.trim()) {
      setTotpError('Please enter a recovery code.');
      return;
    }

    setTotpRecoveryLoading(true);
    setTotpError('');

    try {
      await API.post(GetUserMFAResetRecoveryPathForApi(), {
        email: pendingFirebaseUser?.email || '',
        recoveryCode: totpRecoveryCode.trim(),
      });
      setTotpRecoverySuccess(true);
    } catch (err) {
      console.error('Error verifying recovery code:', err);
      if (err.response?.status === 400) {
        setTotpError('Invalid recovery code. Please try again.');
      } else {
        setTotpError('Something went wrong. Please try again.');
      }
    } finally {
      setTotpRecoveryLoading(false);
    }
  }, [totpRecoveryCode, pendingFirebaseUser]);

  // Main sign-in flow
  const handleSignin = useCallback(async () => {
    if (quickBooksError) {
      tryCloseWindowOrRedirectToRoot();
      return;
    }

    if (!quickBooksAuthorizationCode) {
      return HandleSignIn();
    }

    if (quickBooksSignInState.state !== GetQuickBooksSignInState().state) {
      handleApiError({response:{data:{message:"Sign-in state invalid"}}});
      return;
    }

    const oAuthScopes = (quickBooksSignInState.isCompanyConnect)
      ? GetScopesForSignInAndConnect()
      : GetScopesForSignIn();

    try {
      // Step 1: Exchange Intuit OAuth code for Firebase custom token
      const intuitAuthResponse = await API.post(GetUserIntuitAuthPathForApi(), {
        oAuthCode: quickBooksAuthorizationCode,
        oAuthRealmID: quickBooksRealmID,
        oAuthScopes: oAuthScopes,
        oAuthRedirectUri: GetRedirectUriForSignIn(),
        browserFingerprint: _browserFingerprint,
        isMobile: IsMobile(),
      });

      const { customToken, intuitSignInInfo } = intuitAuthResponse.data;

      // Step 2: Sign in to Firebase with custom token
      const userCredential = await signInWithCustomToken(auth, customToken);

      // Step 3: Call doFirebaseLogin which creates the server session
      await doFirebaseLogin(userCredential.user, undefined, intuitSignInInfo);
    } catch (err) {
      handleApiError(err);
    }
  }, [quickBooksError, quickBooksAuthorizationCode, quickBooksSignInState, quickBooksRealmID, handleApiError, tryCloseWindowOrRedirectToRoot, doFirebaseLogin]);

  useEffect(() => {
    handleSignin();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (redirectToReferrer === true) {
    const from = quickBooksSignInState.from || GetUserHomePath();
    return (
      <Redirect to={from} />
    );
  }

  const progressIndicator = (showProgressIndicator)
    ? ( <ProgressIndicator showImmediately /> )
    : null;

  return (
    <div className={classes.root}>
      {progressIndicator}

      <MultiUseDialog Details={dialogDetails} />

      {/* Server-side TOTP Verification Dialog */}
      <Dialog open={showTOTPDialog} onClose={handleTOTPCancel} maxWidth="xs" fullWidth>
        {totpRecoverySuccess ? (
          <>
            <DialogTitle>MFA Reset Complete</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="primary" style={{ marginBottom: 16 }}>
                Multi-factor authentication has been reset successfully.
              </Typography>
              <Typography variant="body2">
                Please sign in again. You will no longer be prompted for an authenticator code.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" color="primary" onClick={handleTOTPCancel}>
                OK
              </Button>
            </DialogActions>
          </>
        ) : totpShowRecovery ? (
          <>
            <DialogTitle>Use Recovery Code</DialogTitle>
            <DialogContent>
              {totpError && (
                <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                  {totpError}
                </Typography>
              )}

              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Enter one of the recovery codes you saved when you set up MFA.
              </Typography>

              <TextField
                fullWidth
                label="Recovery code"
                value={totpRecoveryCode}
                onChange={(e) => setTotpRecoveryCode(e.target.value)}
                placeholder="xxxx-xxxx"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && totpRecoveryCode.trim()) {
                    handleTOTPRecoveryVerify();
                  }
                }}
                style={{ marginBottom: 8 }}
              />

              <Typography variant="body2" color="textSecondary">
                After resetting, you can sign in without an authenticator code and optionally re-enroll.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setTotpShowRecovery(false); setTotpRecoveryCode(''); setTotpError(''); }} disabled={totpRecoveryLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleTOTPRecoveryVerify}
                disabled={totpRecoveryLoading || !totpRecoveryCode.trim()}
              >
                {totpRecoveryLoading ? 'Resetting...' : 'Reset MFA'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Multi-Factor Authentication</DialogTitle>
            <DialogContent>
              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Enter the 6-digit code from your authenticator app.
              </Typography>

              {totpError && (
                <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                  {totpError}
                </Typography>
              )}

              <TextField
                fullWidth
                value={totpCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTotpCode(val);
                }}
                placeholder="000000"
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && totpCode.length === 6) {
                    handleTOTPVerify();
                  }
                }}
                style={{ marginBottom: 16 }}
              />

              <Link
                component="button"
                variant="body2"
                onClick={() => { setTotpShowRecovery(true); setTotpError(''); }}
                style={{ display: 'block', textAlign: 'center' }}
              >
                Can't access your authenticator?
              </Link>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleTOTPCancel} disabled={totpLoading}>Cancel</Button>
              <Button
                onClick={handleTOTPVerify}
                variant="contained"
                color="primary"
                disabled={totpLoading || totpCode.length !== 6}
              >
                {totpLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

IntuitSignIn.propTypes = {
  classes: PropTypes.object.isRequired,
  signInOnly: PropTypes.bool,
};

export default withStyles(styles, { withTheme: true })(IntuitSignIn);
